import type { BatchLeaderboard } from '@/lib/api/lms';
import { useReadinessFormula } from '@/lib/hooks/useReadiness';
import { tierLabel } from '@/features/Readiness';
import { TIER_LABELS } from './TierBadge';

const TIER_KEYS = ['A', 'B', 'C', 'needs_support'] as const;

// The shared ProgressRing (@kodprep/platform-ui) hard-codes a "NN%" centre
// label with no way to override just the suffix, and other pages depend on
// that behaviour. Readiness is a 0-100 score, not a percentage, so this ring
// is rendered locally rather than changing the shared component.
function ReadinessRing({ value, size = 112 }: { value: number; size?: number }) {
  const pct = Math.max(0, Math.min(100, Math.round(value)));
  const stroke = 7;
  const r = (size - stroke * 2) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - pct / 100);
  const center = size / 2;

  return (
    <div
      className="relative flex-none"
      style={{ width: size, height: size }}
      role="img"
      aria-label={`Readiness score ${pct} out of 100`}
    >
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={center} cy={center} r={r} fill="none" strokeWidth={stroke} className="stroke-muted" />
        <circle
          cx={center}
          cy={center}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          className="stroke-primary"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <span className="text-[34px] font-bold leading-none tabular-nums text-foreground">{pct}</span>
        <span className="mt-1.5 text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
          Readiness
        </span>
      </div>
    </div>
  );
}

export function MyStandingCard({ data }: { data: BatchLeaderboard }) {
  // Tier names are the API's to define (they ship with the formula and its
  // cutoffs); TIER_LABELS is only the offline fallback.
  const { data: formula } = useReadinessFormula();
  const me =
    data.neighbourhood.find((e) => e.is_caller) ?? data.top.find((e) => e.is_caller);
  if (!me || data.caller_rank === 0) return null;

  const atTop = data.caller_rank === 1;
  const tier = TIER_KEYS.includes(me.tier as (typeof TIER_KEYS)[number])
    ? (me.tier as (typeof TIER_KEYS)[number])
    : 'C';

  // The name of whoever is directly ahead of the caller makes the gap
  // concrete ("+6 points to pass Tarun Bose"). That student may not be in
  // either the top list or the neighbourhood window (e.g. caller sits just
  // outside both), so fall back to the rank number rather than guessing.
  const aboveMe = [...data.top, ...data.neighbourhood].find(
    (e) => e.rank === data.caller_rank - 1,
  );

  return (
    <section aria-label="Your standing">
      <p className="text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">
        Your standing
      </p>
      <div className="mt-3.5 flex items-center gap-4">
        <ReadinessRing value={me.readiness_score} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline text-[22px] leading-none">
            <span className="font-bold tabular-nums text-foreground">{data.caller_rank}</span>
            <span className="ml-1 font-medium tabular-nums text-muted-foreground">
              / {data.batch.total_students}
            </span>
          </div>
          {/* A percentile is meaningless (and actively misleading) in a
              near-solo cohort — percentile(1,1) = 100, so a lone student's
              best possible outcome would read as "Top 100%", i.e. last
              place. Suppress the line rather than change the server's
              percentile math, which is correct for real cohorts. */}
          {data.batch.total_students > 2 && (
            <p className="mt-1 text-[13px] text-muted-foreground">
              Top {data.caller_percentile}% of {data.scope_label}
            </p>
          )}
          <span className="mt-2 inline-block rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
            {tierLabel(me.tier, formula) ?? TIER_LABELS[tier]}
          </span>
        </div>
      </div>

      {atTop ? (
        <p className="mt-4 text-[13.5px] font-semibold text-primary">Top of your cohort — hold it.</p>
      ) : (
        <p className="mt-4 text-[13.5px] text-muted-foreground">
          {/* No proportional bar here — points_to_next_rank has no real
              denominator to render an honest fill against (see review Item
              3). The exact number is more honest than a fake gauge. */}
          <span className="font-bold tabular-nums text-foreground">
            +{data.points_to_next_rank} point{data.points_to_next_rank === 1 ? '' : 's'}
          </span>{' '}
          {aboveMe ? `to pass ${aboveMe.username}` : `to reach rank ${data.caller_rank - 1}`}
        </p>
      )}
    </section>
  );
}
